import { CelebrationPayload } from './models/celebration.model';

/**
 * Prioridad de cada tipo de celebración a la hora de elegir cuál se muestra cuando llegan varias
 * juntas. El proyecto completado manda: es el logro mayor. Después el hito, y por último la tarea
 * suelta (que es la más frecuente y la más «ruidosa»).
 */
const PRIORIDAD: Record<string, number> = {
  project_completed: 3,
  milestone_completed: 2,
  sprint_success: 2,
  okr_key_result_achieved: 2,
  task_completed: 1,
};

export function prioridadDeCelebracion(eventType: string): number {
  return PRIORIDAD[eventType] ?? 0;
}

/**
 * Elige, de entre las celebraciones que llegaron en una misma ventana corta, la que debe mostrarse.
 *
 * <p>Reglas (M25.01, decisión de solape hito+proyecto):
 * <ul>
 *   <li><b>La de mayor prioridad gana</b> y sustituye a las demás, en vez de apilarse. Si llegan
 *       juntas la de proyecto y la de hito/tarea del mismo cierre, sólo se ve la de proyecto.</li>
 *   <li><b>Deduplica</b> {@code project_completed} repetidas (mismo tipo + mismo título) dentro de la
 *       ventana, para que un lote o una reconexión no pinte el mismo «Proyecto completado» dos veces.</li>
 * </ul>
 */
export function elegirCelebracion(candidatas: CelebrationPayload[]): CelebrationPayload | null {
  if (candidatas.length === 0) return null;

  // Deduplica project_completed por (eventType + title) antes de decidir: es el único tipo que
  // puede llegar repetido sin aportar nada nuevo.
  const unicas: CelebrationPayload[] = [];
  const vistos = new Set<string>();
  for (const c of candidatas) {
    const clave = c.eventType === 'project_completed' ? `${c.eventType}::${c.title}` : null;
    if (clave === null) {
      unicas.push(c);
      continue;
    }
    if (vistos.has(clave)) continue;
    vistos.add(clave);
    unicas.push(c);
  }

  return unicas.reduce((ganadora, c) =>
    prioridadDeCelebracion(c.eventType) > prioridadDeCelebracion(ganadora.eventType) ? c : ganadora,
  );
}
